import { getSessionStorage } from '@src/utils/enhancedSessionStorage'

import isBrowser from '../../utils/isBrowser'

import { InventoryResponse } from './inventory.service'

const INVENTORY_STORAGE_KEY = 'inventory_data'

type InventoryData = Record<string, InventoryResponse>

/** Stored shape: variant data plus optional response meta (code, message, statusText) */
type StoredInventorySession = InventoryData & {
  code?: number
  message?: string
  statusText?: string
}

/** Return type: variant data + code when variant exists, otherwise full session data */
export type InventorySessionResult = { variant: InventoryResponse; code?: number } | (StoredInventorySession | null)

/**
 * Get inventory data from sessionStorage.
 * - When variant exists: returns { variant, code } (variant data + response status code).
 * - When variant not found: returns full session data (whatever is present: code, message, statusText, etc.).
 */
export function getInventoryFromSessionStorage(variantId: string): InventorySessionResult {
  try {
    if (!isBrowser()) return null
    const storage = getSessionStorage()
    const session = storage.getItemJSON<StoredInventorySession>(INVENTORY_STORAGE_KEY, { isGlobal: true })

    const variantData = session?.[variantId]
    if (variantData != null) {
      return { variant: variantData, code: session?.code }
    }
    return session
  } catch (error) {
    console.error('Error getting inventory from sessionStorage:', error)
    return null
  }
}

/**
 * Store inventory data in sessionStorage
 * - On success: { data, code } (code = HTTP status e.g. 200)
 * - On error: { data: {}, code, message, statusText?, stack? } (code = HTTP status e.g. 400, 500, or undefined for network errors)
 *
 * `merge` exists because this is ONE GLOBAL key with more than one writer. The default (replace) is
 * correct for the `/active-inventory` response, which is the authoritative answer for the page's own
 * product. But an overlay — the quick-ATC popup — sells a DIFFERENT product on top of that page: writing
 * with replace semantics wiped the host page's entries, so the host's add-to-cart telemetry lost
 * `is_preorder` / `preorder_edd`, while skipping the write entirely cost the POPUP those same fields.
 * Entries are keyed by variant id and the two products' ids never collide, so merging keeps both correct.
 * Response meta (`code`/`message`/`statusText`) is still overwritten — it describes the latest write.
 */
export function setInventoryInSessionStorage(payload: {
  data?: Record<string, InventoryResponse>
  code?: number
  message?: string
  statusText?: string
  /** Merge into existing entries instead of replacing them. Use from overlays; see above. */
  merge?: boolean
}): void {
  try {
    if (!isBrowser()) return
    const { data = {}, code, message, statusText, merge = false } = payload ?? {}
    const storage = getSessionStorage()

    let existingData: InventoryData = {}
    if (merge) {
      const session = storage.getItemJSON<StoredInventorySession>(INVENTORY_STORAGE_KEY, { isGlobal: true })
      if (session) {
        // Strip the response meta so it cannot be re-spread as if it were variant data.
        const { code: _code, message: _message, statusText: _statusText, ...variants } = session
        existingData = variants
      }
    }

    storage.setItemJSON(
      INVENTORY_STORAGE_KEY,
      { ...existingData, ...data, code, message, statusText },
      { isGlobal: true },
    )
  } catch (error) {
    console.error('Error storing inventory in sessionStorage:', error)
  }
}
